import React, { useState, useCallback, useEffect } from 'react';
import { presignS3Key, deleteFiles } from '../../api/mediaApi';
import { useConfirm } from '../common/ConfirmModal';
import VideoPlayer from './VideoPlayer';

const IMAGE_EXTS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'tiff', 'tif'];
const VIDEO_EXTS = ['mp4', 'mov', 'avi', 'mkv', 'webm', 'wmv', 'm4v'];
const AUDIO_EXTS = ['mp3', 'wav', 'aac', 'ogg', 'flac', 'm4a'];

function getFileType(name) {
  const ext = (name || '').split('.').pop().toLowerCase();
  if (IMAGE_EXTS.includes(ext)) return 'image';
  if (VIDEO_EXTS.includes(ext)) return 'video';
  if (AUDIO_EXTS.includes(ext)) return 'audio';
  return 'other';
}

function formatSize(bytes) {
  if (!bytes || bytes === 0) return '—';
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return (bytes / Math.pow(1024, i)).toFixed(i > 0 ? 1 : 0) + ' ' + sizes[i];
}

function FileTypeIcon({ type }) {
  if (type === 'image') return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2">
      <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>
    </svg>
  );
  if (type === 'video') return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6366F1" strokeWidth="2">
      <polygon points="5 3 19 12 5 21 5 3"/>
    </svg>
  );
  if (type === 'audio') return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2">
      <path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/>
    </svg>
  );
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2">
      <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/><polyline points="13 2 13 9 20 9"/>
    </svg>
  );
}

function PreviewModal({ file, url, onClose, onDownload, onPrev, onNext, hasPrev, hasNext }) {
  const type = getFileType(file.name);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') onClose();
    if (e.key === 'ArrowLeft' && hasPrev) onPrev();
    if (e.key === 'ArrowRight' && hasNext) onNext();
  }, [onClose, onPrev, onNext, hasPrev, hasNext]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [handleKeyDown]);

  return (
    <div className="mol-preview-overlay" onClick={onClose}>
      <div className="mol-preview-modal" onClick={(e) => e.stopPropagation()}>
        <div className="mol-preview-modal__header">
          <span className="mol-preview-modal__title">{file.name}</span>
          <div className="mol-preview-modal__header-actions">
            <button className="mol-btn mol-btn--secondary mol-btn--sm" onClick={onDownload}>Download</button>
            <button className="mol-preview-modal__close" onClick={onClose}>&times;</button>
          </div>
        </div>
        <div className="mol-preview-modal__body">
          {hasPrev && (
            <button className="mol-preview-modal__nav mol-preview-modal__nav--prev" onClick={onPrev}>&lsaquo;</button>
          )}
          {type === 'image' && url && (
            <img src={url} alt={file.name} className="mol-preview-modal__image" />
          )}
          {type === 'video' && url && (
            <VideoPlayer src={url} autoPlay className="mol-preview-modal__video" />
          )}
          {type === 'audio' && url && (
            <div className="mol-preview-modal__audio-wrap">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="var(--brand-500)" strokeWidth="1.5">
                <path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/>
              </svg>
              <audio src={url} controls autoPlay className="mol-preview-modal__audio" />
            </div>
          )}
          {type === 'other' && (
            <div className="mol-preview-modal__unsupported">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--ink-300)" strokeWidth="1.5">
                <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/><polyline points="13 2 13 9 20 9"/>
              </svg>
              <p>Preview not available for this file type.</p>
              <button className="mol-btn mol-btn--primary mol-btn--sm" onClick={onDownload}>Download File</button>
            </div>
          )}
          {!url && (type === 'image' || type === 'video' || type === 'audio') && (
            <div className="mol-preview-modal__loading">Loading preview...</div>
          )}
          {hasNext && (
            <button className="mol-preview-modal__nav mol-preview-modal__nav--next" onClick={onNext}>&rsaquo;</button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function FileTable({ files, canDelete, onRefresh }) {
  const [previewIdx, setPreviewIdx] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [urlCache, setUrlCache] = useState({});
  const [deleting, setDeleting] = useState({});
  const [confirm, ConfirmDialog] = useConfirm();

  const loadUrl = async (file) => {
    if (urlCache[file.key]) return urlCache[file.key];
    try {
      const res = await presignS3Key(file.key, 'preview');
      const url = res.data.url;
      const shouldCache = getFileType(file.name) !== 'video' || res.data?.playbackType === 'hls';
      if (shouldCache) {
        setUrlCache((prev) => ({ ...prev, [file.key]: url }));
      }
      return url;
    } catch (err) {
      console.error('Failed to get presigned URL:', err);
      return null;
    }
  };

  const openPreview = async (idx) => {
    setPreviewIdx(idx);
    setPreviewUrl(null);
    const url = await loadUrl(files[idx]);
    setPreviewUrl(url);
  };

  const handleDownload = async () => {
    const file = files[previewIdx];
    try {
      const res = await presignS3Key(file.key, 'download');
      if (res.data?.url) window.open(res.data.url, '_blank');
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  const handleDownloadDirect = async (e, file) => {
    e.stopPropagation();
    try {
      const res = await presignS3Key(file.key, 'download');
      if (res.data?.url) window.open(res.data.url, '_blank');
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  const handleDelete = async (e, file) => {
    e.stopPropagation();
    const ok = await confirm({
      title: 'Delete File',
      message: `Delete "${file.name}"? This cannot be undone.`,
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    setDeleting((prev) => ({ ...prev, [file.key]: true }));
    try {
      await deleteFiles([file.key]);
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Delete failed:', err);
    } finally {
      setDeleting((prev) => ({ ...prev, [file.key]: false }));
    }
  };

  const navigatePreview = async (newIdx) => {
    if (newIdx < 0 || newIdx >= files.length) return;
    setPreviewIdx(newIdx);
    setPreviewUrl(null);
    const url = await loadUrl(files[newIdx]);
    setPreviewUrl(url);
  };

  if (files.length === 0) return null;

  return (
    <>
      <div className="mol-file-table-wrapper">
        <table className="mol-file-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Type</th>
              <th>Size</th>
              <th>Last Modified</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {files.map((file, i) => {
              const type = getFileType(file.name);
              return (
                <tr key={file.key || i} className="mol-file-table__row--clickable" onClick={() => openPreview(i)}>
                  <td className="mol-file-table__name">
                    <FileTypeIcon type={type} />
                    {file.name}
                  </td>
                  <td><span className="mol-file-type-badge">{type}</span></td>
                  <td>{formatSize(file.size)}</td>
                  <td>{file.lastModified ? new Date(file.lastModified).toLocaleDateString() : '—'}</td>
                  <td>
                    <div className="mol-file-table__actions" onClick={(e) => e.stopPropagation()}>
                      <button className="mol-btn mol-btn--ghost mol-btn--xs" onClick={() => openPreview(i)}>Preview</button>
                      <button className="mol-btn mol-btn--ghost mol-btn--xs" onClick={(e) => handleDownloadDirect(e, file)}>Download</button>
                      {canDelete && (
                        <button
                          className="mol-btn mol-btn--danger mol-btn--xs"
                          onClick={(e) => handleDelete(e, file)}
                          disabled={deleting[file.key]}
                        >
                          {deleting[file.key] ? 'Deleting...' : 'Delete'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {previewIdx !== null && files[previewIdx] && (
        <PreviewModal
          file={files[previewIdx]}
          url={previewUrl}
          onClose={() => { setPreviewIdx(null); setPreviewUrl(null); }}
          onDownload={handleDownload}
          onPrev={() => navigatePreview(previewIdx - 1)}
          onNext={() => navigatePreview(previewIdx + 1)}
          hasPrev={previewIdx > 0}
          hasNext={previewIdx < files.length - 1}
        />
      )}
      <ConfirmDialog />
    </>
  );
}
